import React, { useRef, useEffect, useState } from "react";
import { McModal, McButton } from "@maersk-global/mds-react-wrapper/components-core";

export default function PoNumberModal({ open, onClose, chargeId, parentInvoiceNo, poNumber, onSuccess }) {
  const modalRef = useRef(null);
  const poInputRef = useRef(null);
  const chargeIdRef = useRef(null); // Use ref to immediately capture chargeId
  const [choice, setChoice] = useState(null); // yes/no
  const [loading, setLoading] = useState(false); // for API calls
  const [poError, setPoError] = useState("");
  const [currentChargeId, setCurrentChargeId] = useState(chargeId); // Initialize with prop

  // Immediately capture chargeId in ref whenever it changes
  useEffect(() => {
    if (chargeId) {
      chargeIdRef.current = chargeId;
      if (chargeId !== currentChargeId) {
        setCurrentChargeId(chargeId);
      }
    }
  }, [chargeId, currentChargeId]);

     // 👇 Add this here
  const allowedPrefixes = ["5200", "7100", "7200", "7300", "7600"];

  const isFoAllowed =
    poNumber &&
    allowedPrefixes.some(prefix =>
      poNumber.toString().startsWith(prefix)
    );

  console.log("PO Number:", poNumber);
  console.log("Is FO Allowed:", isFoAllowed);


  // Reset modal state when opened
  useEffect(() => {
    if (!modalRef.current) return;
    try { modalRef.current.open = open; } catch { modalRef.current.setAttribute("open", ""); }

    if (open) {
      setChoice(null);
      setLoading(false);
      setPoError("");
      if (poInputRef.current) poInputRef.current.value = "";
    }
  }, [open]);

  // Handle Yes choice - use same FO
// ...existing code...

  // Handle Yes choice - use same FO
  const handleYesChoice = async () => {
    setLoading(true);
    
    // Use multiple fallbacks: state, ref, prop
    const chargeIdToUse = currentChargeId || chargeIdRef.current || chargeId;
    
    const payload = {
      chargeNoticeId: chargeIdToUse,
      sameFO: true,
      poNumber: null // Not needed when sameFO is true
    };

    try {
      const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/update-po`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` ,"consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY},
        body: JSON.stringify(payload)
      });
      
      

      if (!res.ok) throw new Error("Network response was not ok");
      
      const data = await res.json();
      
      if (data.status === "success") {
        alert("PO Number updated successfully using FO number!");
        onSuccess?.(chargeIdToUse, data.updatedPONumber);
        onClose();
      } else {
        alert(data.message || "Failed to update PO Number");
        setChoice("no"); // Allow manual input if FO fetch fails
      }
    } catch (err) {
      console.error("Error:", err);
      alert("Failed to update PO Number. Please try again or enter manually.");
      setChoice("no"); // Allow manual input on error
    } finally {
      setLoading(false);
    }
  };

  // Handle manual PO Number submission
  const handleManualSubmit = async () => {
    const manualPONumber = poInputRef.current.value.trim();
    if (!manualPONumber) {
      alert("Please enter the PO Number before submitting");
      return;
    }

    const TMS_PREFIXES = ["5200", "7100", "7200", "7300", "7600"];
    const matchedPrefix = TMS_PREFIXES.find(prefix => manualPONumber.startsWith(prefix));
    if (matchedPrefix) {
      setPoError(
        `PO Numbers starting with "${matchedPrefix}" are TMS-generated and cannot be entered manually. ` +
        `Restricted prefixes: ${TMS_PREFIXES.join(", ")}.`
      );
      return;
    }

    if (manualPONumber.startsWith("00")) {
      setPoError("PO Numbers starting with \"00\" are not allowed.");
      return;
    }

    if (manualPONumber.length < 10) {
      setPoError(`PO Number must be at least 10 digits long. You entered ${manualPONumber.length} digit(s).`);
      return;
    }

    setPoError("");
    setLoading(true);

    // Use multiple fallbacks: state, ref, prop
    const chargeIdToUse = currentChargeId || chargeIdRef.current || chargeId;
    
    const payload = {
      chargeNoticeId: chargeIdToUse,
      sameFO: false,
      poNumber: manualPONumber
    };

    try {
      const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/update-po`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}`,"consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Network response was not ok");
      
      const data = await res.json();
      
      if (data.status === "success") {
        alert("PO Number updated successfully!");
        onSuccess?.(chargeIdToUse, data.updatedPONumber);
        onClose();
      } else {
        alert(data.message || "Failed to update PO Number");
      }
    } catch (err) {
      console.error("Error:", err);
      alert("Failed to update PO Number. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  // Handle No choice - show manual input
  const handleNoChoice = () => {
    setChoice("no");
  };

  return (
    <McModal ref={modalRef} heading="Confirm PO Number">
      <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
        <h3>
          Please confirm whether this charge relates to the same FO number: 
        </h3>

        {!choice && !loading && (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <McButton appearance="primary" disabled={!isFoAllowed} onClick={handleYesChoice}>
              Yes (Use FO as PO)
            </McButton>
            <McButton appearance="secondary" onClick={handleNoChoice}>
              No (Enter manually)
            </McButton>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "1rem" }}>
            <div className="loader" style={{ margin: "0 auto" }}></div>
            <p>Processing PO number...</p>
          </div>
        )}

        {choice === "no" && !loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <label htmlFor="poNumber">Enter PO Number</label>
            <input
              ref={poInputRef}
              id="poNumber"
              type="text"
              placeholder="Enter PO Number"
              style={{
                padding: "0.5rem",
                borderRadius: "4px",
                border: `1px solid ${poError ? "#dc2626" : "#ccc"}`,
              }}
              onChange={() => { if (poError) setPoError(""); }}
            />
            {poError && (
              <p style={{ color: "#dc2626", fontSize: "12px", margin: 0 }}>
                {poError}
              </p>
            )}
            <McButton appearance="primary" onClick={handleManualSubmit}>
              Submit
            </McButton>
          </div>
        )}
      </div>
    </McModal>
  );
}